import React from "react";
import { format, startOfWeek, endOfWeek } from "date-fns";
import { Paper } from "@mui/material";

import ANLoadingScreen from "elements/ANLoadingScreen";
import useClinicianGetHours from "hooks/api/clinician/useClinicianGetHours";
import { ModuleContainer } from "../styles";
import SummaryWeekModule from "./SummaryWeekModule";

const getTotalHours = (hours) => {
  if (!hours || !Array.isArray(hours)) return 0;

  const totalHours = hours?.reduce((acc, curr) => acc + parseFloat(curr.total_duration), 0);

  return totalHours;
};

/**
 * @component
 * @description `SummaryWeek` component displays delivered hours (completed services from their AN calendar)
 * over total authorized hours across all their assigned clients during the current week.
 * @param {string} props.clinicianId - The clinician id.
 * @param {string} props.employmentType - The employment type of the clinician.
 * @param {boolean} props.isSmallScreen - Whether the screen is small.
 * @returns {React.ReactNode} The SummaryWeek component.
 */
const SummaryWeek = ({ clinicianId, employmentType, isSmallScreen }) => {
  // start from monday of the current week and end on sunday
  const startDate = startOfWeek(new Date(), { weekStartsOn: 1 });
  const endDate = endOfWeek(new Date(), { weekStartsOn: 1 });

  const { hours, isHoursLoading } = useClinicianGetHours(clinicianId, {
    authorized: true,
    scheduled: true,
    delivered: true,
    cancellations: true,
    noShows: true,
    startDate: format(startDate, "MM/dd/yyyy"),
    endDate: format(endDate, "MM/dd/yyyy"),
    period: "1 week",
  });

  // Get the total hours missed in the last period to subtract from the scheduled hours
  const clinicianCancellationsHours = getTotalHours(hours?.cancellations?.clinician);
  const noShowsHours = getTotalHours(hours?.noShows);
  const otherHours = getTotalHours(hours?.cancellations?.other);
  const clientCancellationsHours = getTotalHours(hours?.cancellations?.client);

  const totalCancellationsHours =
    clinicianCancellationsHours + noShowsHours + otherHours + clientCancellationsHours;

  const hasAuthorizedHours =
    hours?.authorized && hours?.authorized.find((hour) => parseFloat(hour.total_duration) > 0);

  const authorizedHours = hasAuthorizedHours ? parseFloat(hasAuthorizedHours.total_duration) : 0;
  const deliveredHours = getTotalHours(hours?.delivered);
  const scheduledHours = getTotalHours(hours?.scheduled) - totalCancellationsHours;

  if (isHoursLoading) {
    return (
      <Paper component="section" elevation={2} sx={ModuleContainer}>
        <ANLoadingScreen height="100%" />
      </Paper>
    );
  }

  return (
    <SummaryWeekModule
      scheduledHours={scheduledHours}
      deliveredHours={deliveredHours}
      authorizedHours={authorizedHours}
      employmentType={employmentType}
      isSmallScreen={isSmallScreen}
    />
  );
};

export default SummaryWeek;
